import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Building2, Phone, Headset, Type, Save } from "lucide-react";

const GROUPS = [
  {
    title: "Company Identity", icon: Building2,
    fields: [
      ["company_name", "Legal Company Name", "e.g. LS-India Financial Services Pvt. Ltd."],
      ["copyright_entity", "Footer Copyright Name", "Name shown in footer copyright"],
      ["cin", "CIN", "Corporate Identity Number"],
      ["gstin", "GSTIN", "GST Identification Number"],
      ["registrations", "Applicable Registrations / Licences", "e.g. DSA / LSP details"],
      ["website", "Website", "https://loansupportindia.com"],
    ],
  },
  {
    title: "Contact & WhatsApp", icon: Phone,
    fields: [
      ["phone", "Telephone", "e.g. +91 98765 43210"],
      ["email", "Official Email", "e.g. info@loansupportindia.com"],
      ["address", "Registered Office Address", "Full postal address", true],
      ["support_contact", "Customer Support Contact", "e.g. support hours / number"],
      ["whatsapp", "WhatsApp Number", "Country code + number, no + (e.g. 917980074801)"],
    ],
  },
  {
    title: "Homepage Text", icon: Type,
    fields: [
      ["tagline", "Hero Tagline", "e.g. Let's Make Your Dreams Together"],
      ["subtitle", "Hero Subtitle", "Short paragraph under the tagline", true],
    ],
  },
  {
    title: "Grievance Officer", icon: Headset,
    fields: [
      ["grievance_contact", "Grievance Contact (short)", "Shown on Company Information page"],
      ["grievance_officer_name", "Officer Name / Designation", ""],
      ["grievance_officer_email", "Officer Email", ""],
      ["grievance_officer_phone", "Officer Phone", ""],
      ["grievance_officer_address", "Officer Postal Address", "", true],
      ["grievance_response_time", "Expected Response Time", "e.g. within 7 working days"],
    ],
  },
];

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/settings").then(({ data }) => { if (active) { setForm(data); setLoading(false); } })
      .catch(() => setLoading(false));
    return () => { active = false; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", form);
      await refresh();
      toast.success("Website content updated. Changes are now live on the site.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <p className="py-12 text-center text-muted-foreground">Loading…</p>;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold">Website Content</h2>
          <p className="text-sm text-muted-foreground">Edit company, contact, WhatsApp and grievance details shown across the site.</p>
        </div>
        <Button onClick={save} disabled={saving}><Save className="mr-1.5 h-4 w-4" />{saving ? "Saving…" : "Save Changes"}</Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {GROUPS.map((g) => (
          <Card key={g.title} className="border p-6">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary"><g.icon className="h-4 w-4" /></span>
              {g.title}
            </h3>
            <div className="space-y-4">
              {g.fields.map(([key, label, ph, multiline]) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`s-${key}`}>{label}</Label>
                  {multiline
                    ? <Textarea id={`s-${key}`} rows={2} value={form[key] || ""} onChange={set(key)} placeholder={ph} />
                    : <Input id={`s-${key}`} value={form[key] || ""} onChange={set(key)} placeholder={ph} />}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving}><Save className="mr-1.5 h-4 w-4" />{saving ? "Saving…" : "Save Changes"}</Button>
      </div>
    </div>
  );
}
